// What the 41 location pages (/daikin-dealer-noida, /daikin-vrv-dealer-gurgaon
// and the rest) add on top of their own text in LocationPage.jsx.
//
// ── WHY (Clarity, Oct 2026) ───────────────────────────────────────────────
//
// These pages rank well and bring the most sessions, but:
//   - The "Expert Services Offered" cards lifted on hover like links and went
//     nowhere. People clicked "Daikin Split AC" on /daikin-dealer-noida and
//     rage-clicked. Every card is now a real link (serviceLink below).
//   - The VRV pages still said "VRV IV". They now show the VRV range exactly
//     as the VRV page does (src/data/vrvRange.js, src/components/VRVRange.jsx).
//
// ── RANKING IS LEFT ALONE ─────────────────────────────────────────────────
//
// Nothing here changes a page's URL, title, meta description, H1, intro
// paragraph, areas or links. This only adds sections below them, and the
// same sections go into the pre-rendered HTML (scripts/prerender.js), so
// Google sees more on the page, not less.
//
// No imports: scripts/prerender.js evaluates this file directly.

export const SALES_WA = '917703900307';
export const SERVICE_WA = '919599083770';

// Which page is this? Decides which extra sections it gets.
export function pageKind(slug) {
  const oGeneral = slug.startsWith('o-general');
  return {
    oGeneral,
    // Daikin split ACs are sold online, so these pages show ACs you can buy.
    shop: !oGeneral && /^(daikin-dealer-|daikin-split-ac-dealer-|split-ac-installation$)/.test(slug),
    // Pages where Daikin VRV is the answer, or part of it.
    vrv: /vrv|^hvac-contractor-|^daikin-dealer-/.test(slug),
    // Ceiling units: cassette and ductable.
    ceiling: /cassette|ductable/.test(slug),
    // Engineered systems: chillers, industrial, central plant.
    engineered: /chiller|industrial|hvac-contractor|ductable/.test(slug),
  };
}

const wa = (number, text) => `https://wa.me/${number}?text=${encodeURIComponent(text)}`;

// Where each service card goes. First match wins, so the order matters:
// design work before installation, service before products.
//
// Returns { href, cta, external }.
export function serviceLink(service, slug, place) {
  const s = service.toLowerCase();
  const kind = pageKind(slug);
  const where = place ? ` in ${place}` : '';

  if (/amc|maintenance|repair|breakdown|gas|spare|warranty|support|service(?!s offered)/.test(s)
    && !/installation services/.test(s)) {
    return {
      href: wa(SERVICE_WA, `Hi Citi Cool, I need ${service}${where}.`),
      cta: 'WhatsApp service & repairs',
      external: true,
    };
  }
  if (/design|engineering|load|audit|energy|optimi|retrofit|upgrade|balancing|diffuser|grill|duct fabrication|ducting|plant/.test(s)) {
    return { href: '/hvac-consultant', cta: 'HVAC design, from ₹99' };
  }
  if (/exchange/.test(s) && !kind.oGeneral) {
    return { href: '/daikin-ac-price-list-delhi', cta: 'Up to ₹7,000 off your old AC' };
  }
  if (/install|commissioning|pip|electrical|testing|drainage|ceiling integration|multi-zone|turnkey/.test(s)) {
    if (kind.vrv || kind.ceiling || kind.engineered) {
      return { href: '/architects-interior-designers#installation', cta: 'How we install, 12 checks' };
    }
    return { href: '/blog/ac-installation-cost', cta: 'Installation cost explained' };
  }
  if (/vrv/.test(s)) {
    return { href: '/daikin-vrv-systems', cta: 'See the VRV range' };
  }
  if (/cassette|ducted|ductable|ceiling/.test(s) && !kind.oGeneral) {
    return { href: '/daikin-vrv-systems#indoor-units', cta: 'See how each unit looks' };
  }
  if (/split|inverter|star-rated|star rated/.test(s) && !kind.oGeneral) {
    return { href: '/daikin-ac-price-list-delhi', cta: 'See prices and buy online' };
  }
  if (/chiller|industrial|precision|heavy-duty|central|commercial|residential|cooling/.test(s)) {
    return { href: '/hvac-consultant', cta: 'Talk to an HVAC engineer' };
  }
  return {
    href: wa(SALES_WA, `Hi Citi Cool, I would like a price for ${service}${where}.`),
    cta: 'Get a price on WhatsApp',
    external: true,
  };
}

// Why VRV over a split AC in every room, in plain words. From our VRV page.
export const VRV_VS_SPLIT = [
  ['Outdoor units', 'One outdoor unit for the whole floor or home', 'One outdoor unit for every room'],
  ['Outside your building', 'Clean walls, no row of units', 'A unit on the wall for each AC'],
  ['Indoor look', 'Hidden in the ceiling, flat ceiling panel or on the wall', 'On the wall'],
];

// Free tools, shown on every location page.
export const TOOLKIT = [
  { label: 'AC size calculator', href: '/ac-calculator', text: 'What tonnage your room needs, with ACs in stock' },
  { label: 'Compare ACs', href: '/compare-ac', text: 'Two or three ACs side by side, feature by feature' },
  { label: 'VRV indoor unit corner', href: '/daikin-vrv-systems#indoor-units', text: 'How each VRV indoor unit looks in a room' },
  { label: 'Ceiling space checker', href: '/architects-interior-designers#ceiling', text: 'How low your false ceiling can go' },
  { label: 'HVAC design', href: '/hvac-consultant', text: '₹99 video call, site visit, heat load and BOQ' },
  { label: 'AC guides', href: '/blog', text: 'Inverter or not, 3 or 5 star, and other plain answers' },
];
